import { computed } from 'vue'
import { getDateDifference, setupTranslationPrefixer } from '@/composables/util'

const facetPreparers = {
  animalSubCategory: () => (value) => value,
  entries: () => (value) => value,
  plainText: () => (value) => value,
  plainTextList: () => (value) => value,
  merge:
    ({ t_, te_ }) =>
    (value) =>
      te_(value) ? t_(value) : value,
  translate:
    ({ t_, te_ }) =>
    (value) =>
      te_(value) ? t_(value) : value,
}

const detailPreparers = {
  address:
    ({ handle }) =>
    (details) =>
      [details[handle].streetName, details[handle].postalCode, details[handle].city].filter((value) => value),
  addressCity:
    ({ handle }) =>
    (details) => [details[handle].city],
  animalSubCategory: () => (details) => [details.animalSubCategory],
  date:
    ({ handle, i18n }) =>
    (details) =>
      [details[handle]].filter((value) => value).map((value) => i18n.d(new Date(value), 'medium-date')),
  days:
    ({ handle, tc_ }) =>
    (details) =>
      [details[handle]].filter((value) => value).map((value) => tc_(handle, getDateDifference(value).days)),
  departmentCity:
    ({ handle }) =>
    (details) => [details[handle].department.address.city],
  entries:
    ({ handle }) =>
    (details) =>
      (Array.isArray(details[handle]) ? details[handle] : [details[handle]]).map((value) => (typeof value === 'string' ? value : value?.title)).filter((value) => value),
  lightswitch:
    ({ handle, tc_ }) =>
    (details) => [tc_(details[handle] ? 'yes' : 'no')],
  merge:
    ({ merge, ...context }) =>
    (details) =>
      merge.map((child) => detailPreparers[child.type]({ ...context, ...child })(details).join(', ')).filter((value) => !!value),
  plainText:
    ({ handle }) =>
    (details) =>
      [details[handle]].filter((value) => value),
  plainTextList:
    ({ handle }) =>
    (details) =>
      details[handle],
  translate:
    ({ handle, t_ }) =>
    (details) =>
      (Array.isArray(details[handle]) ? details[handle] : [details[handle]]).filter((value) => value).map((value) => t_(value)),
  years:
    ({ handle, t_, tc_ }) =>
    (details) => [details ? tc_(handle, getDateDifference(details[handle]).years) : t_('years-unknown')],
}

const hasDetailPreparers = {
  address:
    ({ handle }) =>
    (details) =>
      !!(details?.[handle]?.streetName || details?.[handle]?.postalCode || details?.[handle]?.city),
  addressCity:
    ({ handle }) =>
    (details) =>
      !!details?.[handle]?.city,
  animalSubCategory: () => (details) => !!details.animalSubCategory,
  date:
    ({ handle }) =>
    (details) =>
      !!details?.[handle],
  days:
    ({ handle }) =>
    (details) =>
      !!details?.[handle],
  departmentCity:
    ({ handle }) =>
    (details) =>
      !!details?.[handle]?.department?.address?.city,
  entries:
    ({ handle }) =>
    (details) =>
      (Array.isArray(details?.[handle]) ? details[handle] : [details?.[handle]]).map((value) => value?.title).some((value) => value),
  lightswitch: () => () => true,
  merge:
    ({ merge }) =>
    (details) =>
      merge.some((child) => hasDetailPreparers[child.type](child)(details)),
  plainText:
    ({ handle }) =>
    (details) =>
      !!details?.[handle],
  plainTextList:
    ({ handle }) =>
    (details) =>
      Array.isArray(details?.[handle]) && details[handle].length > 0,
  translate:
    ({ handle }) =>
    (details) =>
      !!details?.[handle],
  years: () => () => true,
}

// This mapping co-exists in symfony/src/IkZoekBaas/AnimalSearcher.php
// `elasticSearchValue` is used in the query to retrieve hits
// `elasticFacetValue` is used in the query to retrieve the facets, i.e. aggregations
//
// These need different values because in the search query we need to supply `null` when we want to match
// entries that do not have a date supplied, while in the facet query we need to supply `from 1900-01-01 to 1900-01-02`
// to match the `missing` argument that is used to set date value when it is not supplied.
//
const birthdateMap = {
  'birthdate-missing': { elasticSearchValue: null, elasticFacetValue: 'from 1900-01-01 to 1900-01-02' },
  'birthdate-9': { elasticSearchValue: 'from 1900-01-02 to now-9y/d', elasticFacetValue: 'from 1900-01-02 to now-9y/d' }, // Note that we use 1900-01-01 when a birthdate is missing
  'birthdate-4': { elasticSearchValue: 'from now-9y/d to now-4y/d', elasticFacetValue: 'from now-9y/d to now-4y/d' },
  'birthdate-1': { elasticSearchValue: 'from now-4y/d to now-1y/d', elasticFacetValue: 'from now-4y/d to now-1y/d' },
  'birthdate-0': { elasticSearchValue: 'from now-1y/d', elasticFacetValue: 'from now-1y/d' },
}

const genderIconMap = { male: 'app-male-large', female: 'app-female-large', fallback: 'app-unknown-gender' }

const animalAttributesConfig = [
  { handle: 'filenumber', type: 'plainText', forVisible: ['detail'], forCategories: [], forTypes: [] },
  { handle: 'animalHighlightCategories', type: 'translate', forVisible: ['facet', 'detail'], forCategories: [], forTypes: ['available'] },
  { handle: 'animalSubCategoryBirds', uriName: 'animalSubCategory', type: 'animalSubCategory', forVisible: ['facet', 'detail'], forCategories: ['vogels'], forTypes: [] },
  { handle: 'animalSubCategoryCats', uriName: 'animalSubCategory', type: 'animalSubCategory', forVisible: ['facet', 'detail'], forCategories: ['katten'], forTypes: [] },
  { handle: 'animalSubCategoryDogs', uriName: 'animalSubCategory', type: 'animalSubCategory', forVisible: ['facet', 'detail'], forCategories: ['honden'], forTypes: [] },
  {
    handle: 'animalSubCategoryExLaboratoryAnimals',
    uriName: 'animalSubCategory',
    type: 'animalSubCategory',
    forVisible: ['facet', 'detail'],
    forCategories: ['ex-proefdieren'],
    forTypes: [],
  },
  { handle: 'animalSubCategoryOther', uriName: 'animalSubCategory', type: 'animalSubCategory', forVisible: ['facet', 'detail'], forCategories: ['overige'], forTypes: [] },
  {
    handle: 'animalSubCategoryRabbitsAndRodents',
    uriName: 'animalSubCategory',
    type: 'animalSubCategory',
    forVisible: ['facet', 'detail'],
    forCategories: ['konijnen-en-knagers'],
    forTypes: [],
  },
  {
    handle: 'department',
    type: 'departmentCity',
    highlight: { showLabel: false, icon: () => 'app-location-pin' },
    forVisible: ['highlight'],
    forCategories: [],
    forTypes: ['available'],
  },
  {
    handle: 'animalAddress',
    type: 'addressCity',
    highlight: { showLabel: false, icon: () => 'app-location-pin' },
    forVisible: ['highlight'],
    forCategories: [],
    forTypes: ['found', 'missing'],
  },
  {
    handle: 'animalGender',
    type: 'merge',
    merge: [
      { handle: 'animalGender', type: 'translate' },
      { handle: 'animalGenderExplanation', type: 'plainText' },
    ],
    highlight: { showLabel: false, icon: (value) => genderIconMap[value] || genderIconMap.fallback },
    forVisible: ['facet', 'detail'],
    forCategories: [],
    forTypes: [],
  },
  {
    handle: 'animalGender',
    type: 'translate',
    forVisible: ['card'],
    forCategories: [],
    forTypes: [],
  },
  {
    handle: 'animalCoupleCount',
    type: 'translate',
    forVisible: ['facet'],
    forCategories: [],
    forTypes: ['available'],
  },
  {
    handle: 'birthdate',
    type: 'date',
    highlight: { showLabel: false, icon: () => 'birthday-cake' },
    forVisible: ['detail', 'highlight'],
    forCategories: [],
    forTypes: ['available', 'missing'],
  },
  {
    handle: 'birthdate',
    labelKey: 'age',
    type: 'translate',
    facet: {
      arguments: { missing: '1900-01-01', ranges: Object.entries(birthdateMap).map(([key, { elasticFacetValue }]) => `${key}: ${elasticFacetValue}`) },
      facetName: 'birthdateRange',
      toSearchArgument: (value) => (Array.isArray(value) ? value.map((v) => birthdateMap[v].elasticSearchValue) : birthdateMap[value].elasticSearchValue),
    },
    forVisible: ['facet'],
    forCategories: [],
    forTypes: ['available', 'missing'],
  },
  { handle: 'animalDateInShelter', type: 'days', forVisible: ['detail'], forCategories: [], forTypes: [] },
  { handle: 'animalPlaceableFrom', type: 'date', forVisible: ['detail'], forCategories: [], forTypes: ['available'] },
  {
    handle: 'animalMissingSinceDate',
    type: 'days',
    highlight: { showLabel: true, icon: () => 'calendar' },
    forVisible: ['detail', 'highlight'],
    forCategories: [],
    forTypes: ['missing'],
  },
  { handle: 'animalFoundDate', type: 'date', highlight: { showLabel: true, icon: () => 'calendar' }, forVisible: ['detail', 'highlight'], forCategories: [], forTypes: ['found'] },
  {
    handle: 'animalAlive',
    type: 'lightswitch',
    highlight: { showLabel: true, icon: () => 'app-exclamation-mark' },
    forVisible: ['detail', 'highlight'],
    forCategories: [],
    forTypes: ['found'],
  },
  { handle: 'animalAddress', type: 'address', forVisible: ['detail'], forCategories: [], forTypes: ['found', 'missing'] },
  { handle: 'animalColorDescription', type: 'plainText', forVisible: ['facet', 'detail'], forCategories: [], forTypes: ['found', 'missing'] },
  { handle: 'animalCollarState', type: 'translate', forVisible: ['detail'], forCategories: [], forTypes: ['found', 'missing'] },
  { handle: 'animalCollarDescription', type: 'plainText', forVisible: ['detail'], forCategories: [], forTypes: ['found', 'missing'] },
  { handle: 'animalMicrochipType', type: 'translate', forVisible: ['facet', 'detail'], forCategories: [], forTypes: ['found', 'missing'] },
  { handle: 'animalCouple', type: 'entries', forVisible: ['detail'], forCategories: [], forTypes: [] },
  { handle: 'animalWithChildren', type: 'plainText', forVisible: ['facet', 'detail'], forCategories: [], forTypes: ['available'] },
  { handle: 'animalWithOtherConspecifics', type: 'translate', forVisible: ['facet', 'detail'], forCategories: [], forTypes: ['available'] },
  {
    handle: 'animalWithDogs',
    type: 'merge',
    merge: [
      { handle: 'animalWithDogs', type: 'translate' },
      { handle: 'animalWithDogsExplanation', type: 'plainText' },
    ],
    forVisible: ['facet', 'detail'],
    forCategories: ['katten', 'honden'],
    forTypes: ['available'],
  },
  {
    handle: 'animalWithCats',
    type: 'merge',
    merge: [
      { handle: 'animalWithCats', type: 'translate' },
      { handle: 'animalWithCatsExplanation', type: 'plainText' },
    ],
    forVisible: ['facet', 'detail'],
    forCategories: ['katten', 'honden'],
    forTypes: ['available'],
  },
  {
    handle: 'animalSpecialCare',
    type: 'merge',
    merge: [
      { handle: 'animalSpecialCare', type: 'plainTextList' },
      { handle: 'animalSpecialCareExplanation', type: 'plainText' },
    ],
    forVisible: ['facet', 'detail'],
    forCategories: ['katten', 'honden'],
    forTypes: ['available'],
  },
  {
    handle: 'animalNeutered',
    type: 'merge',
    merge: [
      { handle: 'animalNeutered', type: 'translate' },
      { handle: 'animalNeuteredExplanation', type: 'plainText' },
    ],
    forVisible: ['facet', 'detail'],
    forCategories: ['katten', 'honden', 'konijnen-en-knagers'],
    forTypes: ['available', 'missing', 'found'],
  },
  {
    handle: 'animalStayHomeAlone',
    type: 'merge',
    merge: [
      { handle: 'animalStayHomeAlone', type: 'translate' },
      { handle: 'animalStayHomeAloneExplanation', type: 'plainText' },
    ],
    forVisible: ['facet', 'detail'],
    forCategories: ['honden'],
    forTypes: ['available'],
  },
  {
    handle: 'animalInCar',
    type: 'merge',
    merge: [
      { handle: 'animalInCar', type: 'translate' },
      { handle: 'animalInCarExplanation', type: 'plainText' },
    ],
    forVisible: ['facet', 'detail'],
    forCategories: ['honden'],
    forTypes: ['available'],
  },
  { handle: 'animalLeashBehaviour', type: 'plainText', forVisible: ['facet', 'detail'], forCategories: ['honden'], forTypes: ['available'] },
  {
    handle: 'animalBasicCommands',
    type: 'merge',
    merge: [
      { handle: 'animalBasicCommands', type: 'translate' },
      { handle: 'animalBasicCommandsExplanation', type: 'plainText' },
    ],
    forVisible: ['facet', 'detail'],
    forCategories: ['honden'],
    forTypes: ['available'],
  },
  { handle: 'animalHouseTrained', type: 'translate', forVisible: ['facet', 'detail'], forCategories: ['honden'], forTypes: ['available'] },
  { handle: 'animalWatchful', type: 'translate', forVisible: ['facet', 'detail'], forCategories: ['honden'], forTypes: ['available'] },
  { handle: 'animalHeight', type: 'plainText', forVisible: ['facet', 'detail'], forCategories: ['honden'], forTypes: ['available'] },
  { handle: 'animalFurCare', type: 'translate', forVisible: ['facet', 'detail'], forCategories: ['honden'], forTypes: ['available'] },
  { handle: 'animalCanRunFree', type: 'translate', forVisible: ['facet', 'detail'], forCategories: ['honden'], forTypes: ['available'] },
  {
    handle: 'animalOutside',
    type: 'merge',
    merge: [
      { handle: 'animalOutside', type: 'translate' },
      { handle: 'animalOutsideExplanation', type: 'plainText' },
    ],
    forVisible: ['facet', 'detail'],
    forCategories: ['katten'],
    forTypes: ['available'],
  },
  {
    handle: 'animalPetted',
    type: 'merge',
    merge: [
      { handle: 'animalPetted', type: 'translate' },
      { handle: 'animalPettedExplanation', type: 'plainText' },
    ],
    forVisible: ['facet', 'detail'],
    forCategories: ['katten'],
    forTypes: ['available'],
  },
  { handle: 'animalBunnyCoat', type: 'translate', forVisible: ['facet', 'detail'], forCategories: ['konijnen-en-knagers'], forTypes: ['available'] },
  {
    handle: 'animalFarm',
    type: 'merge',
    merge: [
      { handle: 'animalFarm', type: 'translate' },
      { handle: 'animalFarmExplanation', type: 'plainText' },
    ],
    forVisible: ['facet', 'detail'],
    forCategories: ['katten'],
    forTypes: ['available'],
  },
  { handle: 'animalAvailability', type: 'translate', forVisible: ['detail'], forCategories: [], forTypes: ['available'] },

  /* Add a facet filter for the department */
  { handle: 'site', facet: { facetName: 'site' }, type: 'translate', forVisible: ['facet'], forCategories: [], forTypes: ['available', 'found'] },
]

function setupAnimalTranslators() {
  return {
    labelTranslators: setupTranslationPrefixer('Animal', 'attribute-label'),
    valueTranslators: setupTranslationPrefixer('Animal', 'attribute-value'),
  }
}

/**
 * Returns the tools to translate attribute handle and its specific values
 */
export function getAnimalAttributeTranslations(handle, visible = '') {
  const i18n = useI18n()
  const { labelTranslators, valueTranslators } = setupAnimalTranslators()
  const enabledFilters = animalAttributesConfig.filter(function (config) {
    return visible.length === 0 || (visible.length > 0 && config.forVisible.includes(visible))
  })
  for (const config of enabledFilters) {
    if (config.handle === handle) {
      const getValues = detailPreparers[config.type]({ ...valueTranslators, ...config, i18n })
      return {
        getValue: (details) => getValues(details).join(', '),
        getValues,
        hasValue: hasDetailPreparers[config.type](config),
        label: labelTranslators.t_(config.labelKey || config.handle),
      }
    }
  }

  // Handle animalSubCategory
  if (handle === 'animalSubCategory') {
    const getValues = detailPreparers.plainText({ ...valueTranslators, handle, i18n })
    return {
      getValue: (details) => getValues(details).join(', '),
      getValues,
      hasValue: hasDetailPreparers.plainText({ handle }),
      label: labelTranslators.t_(handle),
    }
  }

  // Fallback to translate type
  const getValues = detailPreparers.translate({ ...valueTranslators, handle, i18n })
  return {
    getValue: (details) => getValues(details).join(', '),
    getValues,
    hasValue: hasDetailPreparers.translate({ handle }),
    label: labelTranslators.t_(handle),
  }
}

export function setupFacetValueToLabel() {
  const i18n = useI18n()
  const { valueTranslators } = setupAnimalTranslators()
  return ({ type, handle }, value) => facetPreparers[type]({ ...valueTranslators, handle, i18n })(value)
}

/**
 * Returns the animal attributes available for filtering on the AnimalOverview template
 */
export function setupAnimalFacetConfigs(animalCategory, animalType) {
  const i18n = useI18n()
  const { labelTranslators, valueTranslators } = setupAnimalTranslators()
  return animalAttributesConfig
    .filter(
      (config) =>
        (config.forVisible.length === 0 || config.forVisible.includes('facet')) &&
        (config.forCategories.length === 0 || config.forCategories.includes(animalCategory)) &&
        (config.forTypes.length === 0 || config.forTypes.includes(animalType)),
    )
    .map((config) => ({
      handle: config.handle,
      type: config.type,
      // the property name how it is presented in the uri
      uriName: config.uriName || config.handle,
      // facetArguments is used for optional `facets: birthday(ranges: ['from ... to ...'])` argument
      facetArguments: config.facet?.arguments,
      // facetName is used as the name in the `elasticSearch(site: ..., birthdayFacet: ...)` argument
      // todo: consider renaming facetName to `searchFacetName`
      facetName: config.facet?.facetName || `${config.handle}Facet`,
      toSearchFacetArgument: config.facet?.toSearchArgument || ((value) => value),
      label: labelTranslators.t_(config.labelKey || config.handle),
      getValue: facetPreparers[config.type]({ ...valueTranslators, handle: config.handle, i18n }),
    }))
}

/**
 * Returns the animal attributes available as details on the AnimalDetail template
 */
export function setupAnimalDetailConfigs(animalCategory, animalType) {
  const i18n = useI18n()
  const { labelTranslators, valueTranslators } = setupAnimalTranslators()
  return computed(() =>
    animalAttributesConfig
      .filter(
        (config) =>
          (config.forVisible.length === 0 || config.forVisible.includes('detail')) &&
          (config.forCategories.length === 0 || config.forCategories.includes(animalCategory)) &&
          (config.forTypes.length === 0 || config.forTypes.includes(animalType)),
      )
      .map((config) => ({
        handle: config.handle,
        label: labelTranslators.t_(config.labelKey || config.handle),
        hasValues: hasDetailPreparers[config.type](config),
        getValues: detailPreparers[config.type]({ ...valueTranslators, ...config, i18n }),
      })),
  )
}

/**
 * Returns the animal attributes available as highlights on the AnimalDetail template
 */
export function setupAnimalHighlightConfigs(animalCategory, animalType) {
  const i18n = useI18n()
  const { labelTranslators, valueTranslators } = setupAnimalTranslators()
  return computed(() =>
    animalAttributesConfig
      .filter(
        (config) =>
          (config.forVisible.length === 0 || config.forVisible.includes('highlight')) &&
          (config.forCategories.length === 0 || config.forCategories.includes(animalCategory)) &&
          (config.forTypes.length === 0 || config.forTypes.includes(animalType)),
      )
      .map((config) => ({
        handle: config.handle,
        label: labelTranslators.t_(config.labelKey || config.handle),
        showLabel: config.highlight.showLabel,
        getIcon: config.highlight.icon,
        hasValues: hasDetailPreparers[config.type](config),
        getValues: detailPreparers[config.type]({ ...valueTranslators, ...config, i18n }),
      })),
  )
}
